import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useRole } from '../../context/RoleContext';
import type { Permission } from '../../context/RoleContext';
import { landingPathForRole } from '../../lib/roleLanding';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
    children: React.ReactNode;
    requiredPermission?: Permission;
}

export function ProtectedRoute({ children, requiredPermission }: ProtectedRouteProps) {
    const { role, loading, can } = useRole();
    const location = useLocation();

    if (loading) {
        return (
            <div className="flex-1 flex items-center justify-center min-h-[50vh]">
                <Loader2 className="w-8 h-8 animate-spin text-accent" />
            </div>
        );
    }

    if (!role) {
        // Not authenticated or role missing, although App.tsx already checks session,
        // it's good to have this as a fallback.
        return <Navigate to="/" state={{ from: location }} replace />;
    }

    if (requiredPermission && !can(requiredPermission)) {
        // Send them somewhere their role can actually load. This used to be a
        // hardcoded /workshop, which is a redirect loop for any role without
        // `workshop` — the accountant, since they were scoped to the books.
        const fallback = landingPathForRole(role);
        if (location.pathname === fallback) {
            // Last resort: the role cannot load its own landing page. Bailing to
            // Settings rather than navigating in a circle — every role has it.
            return <Navigate to="/settings" replace />;
        }
        return <Navigate to={fallback} replace />;
    }

    return <>{children}</>;
}
